import React from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Radio, RadioGroup, Text } from '@fluentui/react-components';
import { ContentViewRegular, ImageMultipleRegular, VideoRegular } from '@fluentui/react-icons';

const iconSize = 24;

const getStyles = () => ({
  titleContainer: {
    marginBottom: 20,
  },
  adFormatContainer: {
    maxWidth: 530,
    marginBottom: 12,
  },
  radioGroup: {
    alignItems: 'stretch !important',
  },
  radioOption: {
    flex: 1,
    background: 'white',
    margin: 3,
    padding: 12,
    borderRadius: 4,
    border: '1px solid #E1DFDD',
    justifyContent: 'start',

    '& .fui-Radio__indicator': {
      position: 'absolute',
      top: 4,
      right: 4,
      display: 'none',

      '& svg': {
        marginLeft: 1.5,
      },
    },

    '& .fui-Radio__label': {
      flex: 1,
    },

    '&.selected .fui-Radio__indicator': {
      display: 'block',
    },

    '&.selected': {
      border: '1px solid #0078D4',
    },
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    minHeight: 135,
  },
  label: {
    display: 'flex',
    fontSize: 16,
    marginBottom: 5,

    '& svg': {
      paddingRight: 4,
    },
  },
});

export const AdFormat = {
  Native: 'Native',
  Display: 'Display',
  Video: 'Video',
};

const OptionConfigs = {
  [AdFormat.Native]: {
    label: _TL_('Native'),
    description: _TL_('Show personalized, responsive, and visually rich ads based on your assets.'),
    getIcon: () => <ContentViewRegular fontSize={iconSize} />,
  },
  [AdFormat.Display]: {
    label: _TL_('Display'),
    description: _TL_('Showcase eye-catching, customized image ads.'),
    getIcon: () => <ImageMultipleRegular fontSize={iconSize} />,
  },
  [AdFormat.Video]: {
    label: _TL_('Video'),
    description: _TL_('Engage viewers as they browse the web and watch videos.'),
    getIcon: () => <VideoRegular fontSize={iconSize} />,
  },
};

export const AdFormatOption = ({
  value,
  onChange,
  i18n,
  disabled,
  showHeader,
}) => {
  const classes = useDefaultStyles(getStyles);

  return (
    <div className={classes.adFormatContainer}>
      {showHeader && (
        <div className={classes.titleContainer}>
          <h2>{i18n.getString(disabled ? _TL_('Edit ads') : _TL_('Create ads'))}</h2>
          <Text>{i18n.getString(_TL_('Show compelling ads to your ideal audience across the Microsoft Advertising Network.'))}</Text>
        </div>
      )}
      <RadioGroup
        className={classes.radioGroup}
        onChange={(ev, data) => onChange(data.value)}
        value={value}
        layout="horizontal-stacked"
        disabled={disabled}
      >
        {_.keys(AdFormat).map(key => (
          <Radio
            className={classNames(classes.radioOption, value === AdFormat[key] && 'selected')}
            key={key}
            value={AdFormat[key]}
            label={
              <div className={classes.labelContainer}>
                <div className={classes.label}>
                  {OptionConfigs[AdFormat[key]].getIcon()}
                  {i18n.getString(OptionConfigs[AdFormat[key]].label)}
                </div>
                <Text>
                  {i18n.getString(OptionConfigs[AdFormat[key]].description)}
                </Text>
              </div>
            }
          />
        ))}
      </RadioGroup>
    </div>
  );
};

AdFormatOption.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
  showHeader: PropTypes.bool,
};

AdFormatOption.defaultProps = {
  disabled: false,
  showHeader: true,
};
